import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { useGlobalStore } from '../stores/globalStore';
import { effectiveMenuHeaderOverride, menuCycleInfoKey } from '../utils/tempMenu';
import type {
  POSPage,
  POSLayout,
  SelectiveMenuFlag,
  UnitMapping,
  PatientSelectiveMenu,
  SelectiveMenuHeader,
  SelectiveMenuAllergenConflict,
  OffMenuAdjustmentCheckItem,
  OffMenuAdjustmentCheckResult,
  SelectiveMenuItem,
  SelectiveMenuPatientRequest,
  SelectiveMenuPatientSave,
  SelectiveMenuEmployeeRequest,
  SelectiveMenuNutrientRequest,
  MenuCycleInfo,
  MenuCategoryPrintOrder,
  MenuCategory,
  NutrientDefault,
  FacilityUnitPrintSchedule,
  FacilityDiningRoom,
  DiningRoomTime,
  DiningRoomTable,
  DiningRoomSeatCache,
  Employee,
  FacilityPrinter,
  PrinterType,
  PrintQueueItem,
  ScheduledPrintItem,
  PrintedItem,
  MenuHeader,
  SelectiveMenuQueueSeatingRequest,
  TallyAdjustment,
  Consistency,
  Portion,
  ItemSize,
  ItemType,
  ServiceItem,
  Item,
  CreditBalance,
  MenuHeaderCost,
  MenuHeaderItemCost,
  ItemEducationCard,
  MenuCategoryUsage,
} from '../types/selectiveTouch';

import type { FacilitySettings } from '../types/facility';
import { toLocalDateString } from '../utils/localDate';

/** Format a date string as yyyy-MM-dd for query params (matches Blazor dateParam format) */
function toDateParam(menuDate: string): string {
  return menuDate.substring(0, 10);
}

// ===== QUERY KEYS =====
export const selectiveTouchKeys = {
  all: ['selectiveTouch'] as const,
  facilitySettings: (facilityId: number) => [...selectiveTouchKeys.all, 'facilitySettings', facilityId] as const,
  pages: (facilityId: number) => [...selectiveTouchKeys.all, 'pages', facilityId] as const,
  layout: (facilityId: number) => [...selectiveTouchKeys.all, 'layout', facilityId] as const,
  flags: (facilityId: number) => [...selectiveTouchKeys.all, 'flags', facilityId] as const,
  unitColors: (facilityId: number) => [...selectiveTouchKeys.all, 'unitColors', facilityId] as const,
  patients: (facilityId: number) => [...selectiveTouchKeys.all, 'patients', facilityId] as const,
  menuCategories: (facilityId: number) => [...selectiveTouchKeys.all, 'menuCategories', facilityId] as const,
  menuCategoryPrintOrder: (facilityId: number) => [...selectiveTouchKeys.all, 'menuCategoryPrintOrder', facilityId] as const,
  menuChoices: (facilityId: number, patientId: number, mealtime: number) =>
    [...selectiveTouchKeys.all, 'menuChoices', facilityId, patientId, mealtime] as const,
};

// ===== FACILITY SETTINGS =====

/** GET facility/GetById - the query function behind useFacilitySettings, for
 *  callers that read another facility's settings through the same cache entry. */
export async function fetchFacilitySettings(facilityId: number): Promise<FacilitySettings> {
  const { data } = await apiClient.get<FacilitySettings>(
    API.facility(facilityId)
  );
  return data;
}

export function useFacilitySettings(facilityId: number) {
  return useQuery({
    queryKey: selectiveTouchKeys.facilitySettings(facilityId),
    queryFn: () => fetchFacilitySettings(facilityId),
    staleTime: 10 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

// ===== MENU CYCLE INFO =====

export async function fetchActiveMenuCycleInfo(
  facilityId: number,
  date: string,
  fKeyMenuHeaderOverride?: number | null,
  /** False: the facility's active cycle even while a Temp Menu is picked. */
  useTempMenu = true,
): Promise<MenuCycleInfo> {
  const override = useTempMenu
    ? effectiveMenuHeaderOverride(fKeyMenuHeaderOverride)
    : effectiveMenuHeaderOverride(fKeyMenuHeaderOverride, null);
  const url = override != null
    ? `${API.activeMenuCycleInfo(facilityId)}?fKeyMenuHeaderOverride=${override}`
    : API.activeMenuCycleInfo(facilityId);
  const { data } = await apiClient.post<MenuCycleInfo>(
    url,
    JSON.stringify(date),
    { headers: { 'Content-Type': 'application/json' } }
  );
  return data;
}

/**
 * React Query wrapper around `fetchActiveMenuCycleInfo` for the
 * Menu Cycle info display in Cost Analysis - Menu and similar
 * generators. Debounces re-fetches while the user is dragging the
 * date spinners by keying the query on the yyyy-MM-dd portion.
 * <p>
 * Optional `fKeyMenuHeaderOverride` scopes the lookup to a specific
 * menu header PKey — mirrors the WinForms
 * `GetMenuCycleInUseInfo(..., override)` overload used by the
 * Employee Production Tally report to display the Employee Menu
 * (facility `FKey_Menu_Header_Employee`) instead of the resident
 * menu cycle. With no explicit override the user's Temp Menu applies
 * (see `effectiveMenuHeaderOverride`); the query key ends with the
 * menu actually asked for, so picking or clearing a Temp Menu refetches.
 * `useTempMenu` false (a report that cannot use the Temp Menu yet, see
 * `TempMenuSupport`) asks for the facility's active cycle even while one is picked.
 */
export function useMenuCycleInfo(
  facilityId: number,
  dateIso: string | null,
  fKeyMenuHeaderOverride?: number | null,
  useTempMenu = true,
) {
  const tempOverride = useGlobalStore((s) => s.tempMenuOverrideId);
  const overrideKey = effectiveMenuHeaderOverride(fKeyMenuHeaderOverride, useTempMenu ? tempOverride : null);
  return useQuery({
    queryKey: menuCycleInfoKey(facilityId, dateIso, overrideKey),
    // overrideKey already holds the Temp Menu when it applies.
    queryFn: () => fetchActiveMenuCycleInfo(
      facilityId,
      dateIso ?? toLocalDateString(),
      overrideKey,
      false,
    ),
    enabled: facilityId > 0 && !!dateIso,
    staleTime: 60 * 1000,
    // Keep the previous cycle info visible while a new date-driven
    // fetch is in flight. Prevents the "Menu Cycle" panel from
    // flashing to a Loading placeholder every time the user
    // adjusts the DateStart on Cost Analysis - Menu.
    placeholderData: keepPreviousData,
  });
}

// ===== CREDIT BALANCE =====

export async function fetchCreditBalance(
  facilityId: number,
  patientId: number
): Promise<CreditBalance> {
  const { data } = await apiClient.get<CreditBalance>(
    API.creditBalance(facilityId, patientId)
  );
  return data;
}

export interface VoidMealChargeInput {
  /** The resident, or the guest when fKey_Patient_Parent is set. */
  fKey_Patient: number;
  /** The resident a guest's meal is billed to. */
  fKey_Patient_Parent: number | null;
  menuDate: string;
  mealtime: number;
  fKey_Menu_Header: number;
}

/** Voids the charges for a sale just rung up. Needs Manage Credits. Resolves to the rows voided. */
export async function voidMealCharge(facilityId: number, input: VoidMealChargeInput): Promise<number> {
  const { data } = await apiClient.put<number>(API.creditsVoidMeal(facilityId), {
    ...input,
    menuDate: toLocalDateString(new Date(input.menuDate)),
  });
  return data;
}

// ===== SETUP DATA QUERIES (fetched once on mount, long staleTime) =====

export function useSelectiveTouchPages(facilityId: number) {
  return useQuery({
    queryKey: selectiveTouchKeys.pages(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<POSPage[]>(
        API.selectiveTouchPages(facilityId)
      );
      return data;
    },
    staleTime: 10 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

export function useSelectiveTouchLayout(facilityId: number) {
  return useQuery({
    queryKey: selectiveTouchKeys.layout(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<POSLayout[]>(
        API.selectiveTouchLayout(facilityId)
      );
      return data;
    },
    staleTime: 10 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

export function useSelectiveTouchFlags(facilityId: number) {
  return useQuery({
    queryKey: selectiveTouchKeys.flags(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<SelectiveMenuFlag[]>(
        API.selectiveTouchFlags(facilityId)
      );
      return data;
    },
    staleTime: 10 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

export function useUnitMappingColors(facilityId: number) {
  return useQuery({
    queryKey: selectiveTouchKeys.unitColors(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<UnitMapping[]>(
        API.selectiveTouchUnitColors(facilityId)
      );
      return data;
    },
    staleTime: 10 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

// ===== MENU CATEGORY DATA =====

export function useMenuCategories(facilityId: number) {
  return useQuery({
    queryKey: selectiveTouchKeys.menuCategories(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<MenuCategory[]>(
        API.menuCategories(facilityId)
      );
      return data;
    },
    staleTime: 10 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

export function useMenuCategoryPrintOrder(facilityId: number) {
  return useQuery({
    queryKey: selectiveTouchKeys.menuCategoryPrintOrder(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<MenuCategoryPrintOrder[]>(
        API.menuCategoryPrintOrder(facilityId)
      );
      return data;
    },
    staleTime: 10 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

// ===== PATIENT LIST (POST with request body) =====

export async function fetchSelectiveMenuPatients(
  facilityId: number,
  request: SelectiveMenuPatientRequest
): Promise<PatientSelectiveMenu[]> {
  const { data } = await apiClient.post<PatientSelectiveMenu[]>(
    API.selectiveMenuPatients(facilityId),
    request
  );
  return data;
}

// ===== MENU CHOICES (POST with request body) =====

/**
 * The resident's menu for one meal, or null when there is none: the API answers 204 when
 * ProductionDAL.GenerateSelectiveMenu builds no menu, as for a meal the resident is not on
 * selective menu for. Axios hands a 204 over as "", which the menu screen read as a menu whose
 * allergy check had failed ("Allergy checking is unavailable for this menu").
 */
export async function fetchSelectiveMenuChoices(
  facilityId: number,
  request: SelectiveMenuPatientRequest
): Promise<SelectiveMenuHeader | null> {
  const { data, status } = await apiClient.post<SelectiveMenuHeader | ''>(
    API.selectiveMenuChoices(facilityId),
    request
  );
  return status === 204 || !data ? null : data;
}

export async function fetchEmployeeSelectiveMenuChoices(
  facilityId: number,
  request: SelectiveMenuEmployeeRequest
): Promise<SelectiveMenuHeader> {
  const { data } = await apiClient.post<SelectiveMenuHeader>(
    API.employeeSelectiveMenuChoices(facilityId),
    request
  );
  return data;
}

// ===== MUTATIONS =====

export function useSaveSelectiveMenu(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (saves: SelectiveMenuPatientSave[]) => {
      const url = API.saveSelectiveMenuChoices(facilityId);
      const { data } = await apiClient.post<boolean>(url, saves);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: selectiveTouchKeys.patients(facilityId) });
    },
  });
}

export function useSaveEmployeeSelectiveMenu(facilityId: number) {
  return useMutation({
    mutationFn: async (request: SelectiveMenuEmployeeRequest) => {
      const { data } = await apiClient.post<boolean>(
        API.saveEmployeeSelectiveMenuChoices(facilityId),
        request
      );
      return data;
    },
  });
}

// ===== MODIFIERS & NUTRIENTS =====

export async function fetchModifiers(
  facilityId: number,
  modHeaderId: number,
  request: SelectiveMenuPatientRequest
): Promise<SelectiveMenuItem[]> {
  const { data } = await apiClient.post<SelectiveMenuItem[]>(
    API.selectiveMenuModifiers(facilityId, modHeaderId),
    request
  );
  return data;
}

/**
 * Allergy conflicts for items that are not on the resident's generated menu
 * (modifiers, off-menu search results). The server runs the same allergy-group
 * check as GetSelectiveMenuChoices; a rejected promise means "not checked".
 */
/**
 * The resident-adjustment check for items picked from the off-menu item search
 * (Never Serve, Next Available, Replace, menu category Modify), run by the server
 * with the shared WinForms rules. Each item comes back accepted (as it should be
 * added), refused, or with the next Yes/No question; call again with the answers.
 * A rejected promise means the check could not be done: do not add the item.
 */
export async function checkOffMenuAdjustments(
  facilityId: number,
  patientId: number,
  menuDate: string,
  mealtime: number,
  items: OffMenuAdjustmentCheckItem[],
): Promise<OffMenuAdjustmentCheckResult[]> {
  const { data } = await apiClient.post<OffMenuAdjustmentCheckResult[]>(
    API.selectiveMenuOffMenuAdjustments(facilityId),
    { fKey_Patient: patientId, menuDate, mealtime, items },
  );
  return data ?? [];
}

export async function checkAllergenConflicts(
  facilityId: number,
  patientId: number,
  itemIds: number[],
): Promise<SelectiveMenuAllergenConflict[]> {
  const { data } = await apiClient.post<SelectiveMenuAllergenConflict[]>(
    API.selectiveMenuAllergenConflicts(facilityId),
    { fKey_Patient: patientId, itemIds },
  );
  return data ?? [];
}

export async function fetchItemNutrients(
  facilityId: number,
  request: SelectiveMenuNutrientRequest
): Promise<SelectiveMenuItem[]> {
  const { data } = await apiClient.post<SelectiveMenuItem[]>(
    API.selectiveMenuNutrients(facilityId),
    request
  );
  return data;
}

// ===== GUEST SCHEDULE =====

export async function addGuests(
  facilityId: number,
  quantity: number,
  request: SelectiveMenuPatientRequest
): Promise<PatientSelectiveMenu[]> {
  const { data } = await apiClient.post<PatientSelectiveMenu[]>(
    API.guestScheduleAdd(facilityId, quantity),
    request
  );
  return data;
}

// ===== DINING ROOM / SEATING =====

export async function fetchFacilityDiningRooms(
  facilityId: number,
  menuHeaderId: number
): Promise<FacilityDiningRoom[]> {
  const { data } = await apiClient.get<FacilityDiningRoom[]>(
    API.diningRoomFacilityMenuRooms(facilityId, menuHeaderId)
  );
  return data;
}

export async function fetchDiningRoomTimes(
  facilityId: number,
  mealtime: number,
  diningRoomId: number
): Promise<DiningRoomTime[]> {
  const { data } = await apiClient.get<DiningRoomTime[]>(
    API.diningRoomTimesByMealtime(facilityId, mealtime, diningRoomId)
  );
  return data;
}

export async function fetchDiningRoomTables(
  facilityId: number,
  diningRoomId: number
): Promise<DiningRoomTable[]> {
  const { data } = await apiClient.get<DiningRoomTable[]>(
    API.diningRoomTables(facilityId, diningRoomId)
  );
  return data;
}

export async function fetchSeatAssignments(
  facilityId: number,
  request: { fKey_LayoutDiningRoomTable: number; fKey_LayoutDiningRoom_Time: number; menuDate: string; mealtime: number; fKey_Menu_Header?: number | null }
): Promise<DiningRoomSeatCache[]> {
  const { data } = await apiClient.post<DiningRoomSeatCache[]>(
    API.diningRoomSeatAssignments(facilityId),
    request
  );
  return data;
}

export async function deletePendingSeat(
  facilityId: number,
  seatNumber: number,
  tableId: number,
  timeId: number,
  mealtime: number,
  menuDate: string
): Promise<boolean> {
  const { data } = await apiClient.delete<boolean>(
    API.diningRoomDeletePendingSeat(facilityId, seatNumber, tableId, timeId, mealtime),
    { params: { menuDate: toDateParam(menuDate) } }
  );
  return data;
}

export async function updatePendingSeat(
  facilityId: number,
  request: {
    seatNumber: number;
    fKey_LayoutDiningRoomTable: number;
    fKey_LayoutDiningRoom_Time: number;
    mealtime: number;
    menuDate: string;
    userId: number;
    userName: string;
    firstName: string;
    lastName: string;
  }
): Promise<boolean> {
  const { data } = await apiClient.put<boolean>(
    API.diningRoomUpdatePendingSeat(facilityId),
    request
  );
  return data;
}

export async function deleteSeat(
  facilityId: number,
  seatNumber: number,
  tableId: number,
  timeId: number,
  mealtime: number,
  menuDate: string,
  fKey_Patient?: number | null
): Promise<boolean> {
  const { data } = await apiClient.delete<boolean>(
    API.diningRoomDeleteSeat(facilityId, seatNumber, tableId, timeId, mealtime),
    { params: { menuDate: toDateParam(menuDate), FKey_Patient: fKey_Patient ?? undefined } }
  );
  return data;
}

export async function clearTable(
  facilityId: number,
  tableId: number,
  timeId: number,
  mealtime: number,
  menuDate: string
): Promise<number> {
  const { data } = await apiClient.delete<number>(
    API.diningRoomClearTable(facilityId, tableId, timeId, mealtime),
    { params: { menuDate: toDateParam(menuDate) } }
  );
  return data;
}

export async function clearTablePending(
  facilityId: number,
  tableId: number,
  timeId: number,
  mealtime: number,
  menuDate: string
): Promise<number> {
  const { data } = await apiClient.delete<number>(
    API.diningRoomClearTablePending(facilityId, tableId, timeId, mealtime),
    { params: { menuDate: toDateParam(menuDate) } }
  );
  return data;
}

export async function updateTempSeats(
  facilityId: number,
  tableId: number,
  numberOfTempSeats: number
): Promise<boolean> {
  const { data } = await apiClient.put<boolean>(
    API.diningRoomUpdateTempSeats(facilityId, tableId, numberOfTempSeats)
  );
  return data;
}

export async function addPendingSeat(
  facilityId: number,
  request: {
    seatNumber: number;
    fKey_LayoutDiningRoomTable: number;
    fKey_LayoutDiningRoom_Time: number;
    fKey_LayoutDiningRoom: number;
    mealtime: number;
    menuDate: string;
    userId: number;
    userName: string;
    firstName: string;
    lastName: string;
    fKey_Menu_Header?: number | null;
    day?: number | null;
  }
): Promise<number> {
  const { data } = await apiClient.post<number>(
    API.diningRoomAddPendingSeat(facilityId),
    request
  );
  return data;
}

export async function assignSeat(
  facilityId: number,
  request: {
    fKey_Patient: number;
    seatNumber: number;
    fKey_LayoutDiningRoomTable: number;
    fKey_LayoutDiningRoom_Time: number;
    fKey_LayoutDiningRoom: number;
    mealtime: number;
    menuDate: string;
    userId: number;
    userName: string;
    firstName: string;
    lastName: string;
    fKey_Menu_Header?: number | null;
    day?: number | null;
  }
): Promise<number> {
  const { data } = await apiClient.post<number>(
    API.diningRoomAssignSeat(facilityId),
    request
  );
  return data;
}

export async function getAssignedPatients(
  facilityId: number,
  menuHeaderId: number,
  mealtime: number,
  menuDate: string
): Promise<number[]> {
  const { data } = await apiClient.get<number[]>(
    API.diningRoomGetAssignedPatients(facilityId, menuHeaderId, mealtime),
    { params: { menuDate: toDateParam(menuDate) } }
  );
  return data ?? [];
}

// ===== EMPLOYEE =====

export async function fetchEmployeeByPKey(
  facilityId: number,
  pKeyEmployee: number
): Promise<Employee | null> {
  try {
    const { data } = await apiClient.get<Employee>(
      API.employeeGetByPKey(facilityId, pKeyEmployee)
    );
    return data;
  } catch (err: any) {
    if (err.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

export async function fetchEmployeeById(
  facilityId: number,
  employeeId: string
): Promise<Employee | null> {
  try {
    const { data } = await apiClient.get<Employee>(
      API.employeeGetById(facilityId, employeeId)
    );
    return data;
  } catch (err: any) {
    if (err.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

export async function checkEmployeeIdExists(
  facilityId: number,
  id: string
): Promise<boolean> {
  const { data } = await apiClient.get<boolean>(
    API.employeeIdExists(facilityId, id)
  );
  return data;
}

export async function addEmployee(
  facilityId: number,
  employee: Employee
): Promise<number> {
  const { data } = await apiClient.post<number>(
    API.employeeAdd(facilityId),
    employee
  );
  return data;
}

// ===== PRINTERS =====

export async function fetchPrinterTypes(
  facilityId: number
): Promise<PrinterType[]> {
  const { data } = await apiClient.get<PrinterType[]>(
    API.printerTypes(facilityId)
  );
  return data;
}

export async function fetchFacilityPrinters(
  facilityId: number,
  menuHeaderId: number,
  userId: number
): Promise<FacilityPrinter[]> {
  const { data } = await apiClient.get<FacilityPrinter[]>(
    API.facilityPrinters(facilityId, menuHeaderId, userId)
  );
  return data;
}

export async function saveUserPrinters(
  facilityId: number,
  userId: number,
  printers: FacilityPrinter[]
): Promise<boolean> {
  const { data } = await apiClient.post<boolean>(
    API.saveUserPrinter(facilityId, userId),
    printers
  );
  return data;
}

// ===== PRINT QUEUE =====

export async function fetchPrintQueueAll(
  facilityId: number,
  request: SelectiveMenuQueueSeatingRequest
): Promise<PrintQueueItem[]> {
  const { data } = await apiClient.post<PrintQueueItem[]>(
    API.printQueueAll(facilityId),
    request
  );
  return data;
}

export async function fetchPrintQueueScheduled(
  facilityId: number,
  request: SelectiveMenuQueueSeatingRequest
): Promise<ScheduledPrintItem[]> {
  const { data } = await apiClient.post<ScheduledPrintItem[]>(
    API.printQueueScheduled(facilityId),
    request
  );
  return data;
}

export async function fetchPrintQueuePrinted(
  facilityId: number,
  request: SelectiveMenuQueueSeatingRequest
): Promise<PrintedItem[]> {
  const { data } = await apiClient.post<PrintedItem[]>(
    API.printQueuePrinted(facilityId),
    request
  );
  return data;
}

export async function clearPrintQueue(
  facilityId: number,
  request: SelectiveMenuQueueSeatingRequest
): Promise<number> {
  const { data } = await apiClient.post<number>(
    API.printQueueClearQueue(facilityId),
    request
  );
  return data;
}

export async function clearPrintQueueTable(
  facilityId: number,
  tableId: number,
  request: SelectiveMenuQueueSeatingRequest
): Promise<number> {
  const { data } = await apiClient.post<number>(
    API.printQueueClearQueueTable(facilityId, tableId),
    request
  );
  return data;
}

export async function clearPrintScheduled(
  facilityId: number,
  request: SelectiveMenuQueueSeatingRequest
): Promise<number> {
  const { data } = await apiClient.post<number>(
    API.printQueueClearScheduled(facilityId),
    request
  );
  return data;
}

/**
 * Remove a single resident from the print queue (served early).
 * Flags census record as ServedEarly so production tally reprints exclude them.
 * Returns the items that were on their tray for tally adjustment display.
 */
export async function removeResidentFromQueue(
  facilityId: number,
  fKeyPatient: number,
  request: SelectiveMenuQueueSeatingRequest
): Promise<TallyAdjustment> {
  const { data } = await apiClient.post<TallyAdjustment>(
    API.printQueueRemoveResident(facilityId, fKeyPatient),
    request
  );
  return data;
}

/** A saved resident meal to print a tray ticket for. */
export interface TicketToPrint {
  fKey_Patient: number;
  fKey_Menu_Header: number;
  menuDate: string;
  mealtime: number;
}

export interface PrintTicketsResult {
  tickets: number;
  printJobs: number;
  /** Not printed: no active print agent, or no printer for this user. */
  skipped: number;
}

/**
 * Print saved residents' tray tickets (Print Queue "Print", Print Table, Print All
 * Scheduled, Reprint). They go to the signed-in user's kiosk printers through the
 * Print Agent, and printed tickets come off the queue.
 */
export async function printTickets(facilityId: number, tickets: TicketToPrint[]): Promise<PrintTicketsResult> {
  const { data } = await apiClient.post<PrintTicketsResult>(API.printTickets(facilityId), tickets);
  return data;
}

/**
 * Get a patient's scheduled time from tblPatient_TTPrintQueue.
 * Returns the ISO datetime string if scheduled, null otherwise.
 */
export async function fetchPatientScheduledTime(
  facilityId: number,
  fKeyPatient: number,
  fKeyMenuHeader: number,
  mealtime: number,
  menuDate: string
): Promise<string | null> {
  const { data } = await apiClient.get<string | null>(
    API.printQueuePatientScheduledTime(facilityId, fKeyPatient, fKeyMenuHeader, mealtime),
    { params: { menuDate } }
  );
  return data;
}

/**
 * The credit a resident's meal was billed or credited with, or 0 when it was not. A guest's meal is
 * billed to their resident (fKey_Patient_Parent). `menuDate` is the meal's date.
 */
export async function fetchMealCredit(
  facilityId: number,
  params: { fKey_Patient: number; fKey_Patient_Parent?: number | null; menuDate: string; mealtime: number; fKey_Menu_Header: number },
): Promise<number> {
  const { data } = await apiClient.get<number>(API.selectiveMenuMealCredit(facilityId), {
    params: { ...params, fKey_Patient_Parent: params.fKey_Patient_Parent || undefined },
  });
  return Number(data) || 0;
}

// ===== DEFAULT NUTRIENTS (DRI Group) =====

export async function fetchDefaultNutrients(
  facilityId: number,
  driGroupId: number
): Promise<NutrientDefault[]> {
  const { data } = await apiClient.get<NutrientDefault[]>(
    API.defaultNutrients(facilityId, driGroupId)
  );
  return data;
}

// ===== FACILITY UNIT PRINT SCHEDULES =====

export async function fetchFacilityUnitPrintSchedules(
  facilityId: number
): Promise<FacilityUnitPrintSchedule[]> {
  const { data } = await apiClient.get<FacilityUnitPrintSchedule[]>(
    API.facilityUnitPrintSchedules(facilityId)
  );
  return data;
}

// ===== MENU HEADERS =====

export async function fetchMenuHeadersActive(
  facilityId: number
): Promise<MenuHeader[]> {
  const { data } = await apiClient.get<MenuHeader[]>(
    API.menuHeadersActive(facilityId)
  );
  return data;
}

export async function fetchMenuHeadersAll(
  facilityId: number
): Promise<MenuHeader[]> {
  const { data } = await apiClient.get<MenuHeader[]>(
    API.menuHeaders(facilityId)
  );
  return data;
}

export async function fetchMenuHeaderCost(
  facilityId: number,
  menuHeaderId: number
): Promise<MenuHeaderCost | null> {
  try {
    const { data } = await apiClient.get<MenuHeaderCost>(
      API.menuHeaderCost(facilityId, menuHeaderId)
    );
    return data;
  } catch {
    return null;
  }
}

export async function fetchMenuHeaderItemCosts(
  facilityId: number,
  menuHeaderId: number
): Promise<MenuHeaderItemCost[]> {
  try {
    const { data } = await apiClient.get<MenuHeaderItemCost[]>(
      API.menuHeaderItemCost(facilityId, menuHeaderId)
    );
    return data;
  } catch {
    return [];
  }
}

// ===== ITEM ADD/MOD REFERENCE DATA =====

export async function searchItems(
  facilityId: number,
  searchString: string,
  menuDate?: string,
  fKeyMenuHeader?: number
): Promise<Item[]> {
  const { data } = await apiClient.get<Item[]>(
    API.itemSearch(facilityId, searchString, menuDate, fKeyMenuHeader)
  );
  return data;
}

export async function fetchConsistencies(
  facilityId: number
): Promise<Consistency[]> {
  const { data } = await apiClient.get<Consistency[]>(
    API.consistencies(facilityId)
  );
  return data;
}

export async function fetchPortions(
  facilityId: number
): Promise<Portion[]> {
  const { data } = await apiClient.get<Portion[]>(
    API.portions(facilityId)
  );
  return data;
}

export async function fetchItemTypes(
  facilityId: number
): Promise<ItemType[]> {
  const { data } = await apiClient.get<ItemType[]>(
    API.itemTypes(facilityId)
  );
  return data;
}

export async function fetchItemSizesByItem(
  facilityId: number,
  itemId: number
): Promise<ItemSize[]> {
  const { data } = await apiClient.get<ItemSize[]>(
    API.itemSizesByItem(facilityId, itemId)
  );
  return data;
}

export async function fetchServiceItems(
  facilityId: number
): Promise<ServiceItem[]> {
  const { data } = await apiClient.get<ServiceItem[]>(
    API.serviceItems(facilityId)
  );
  return data;
}

// ===== ITEM EDUCATION CARD =====

export function useItemEducationCard(facilityId: number, itemId: number | null) {
  return useQuery({
    queryKey: [...selectiveTouchKeys.all, 'educationCard', facilityId, itemId] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<ItemEducationCard>(
        API.itemEducationCard(facilityId, itemId!)
      );
      return data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - nutrition data doesn't change during ordering
    enabled: facilityId > 0 && itemId != null && itemId > 0,
  });
}

// ===== PAGE LAYOUT CRUD =====

export async function savePosPage(
  facilityId: number,
  page: POSPage
): Promise<number> {
  const { data } = await apiClient.post<number>(
    API.selectiveTouchSavePage(facilityId),
    page
  );
  return data;
}

export async function deletePosPage(
  facilityId: number,
  pKey: number
): Promise<void> {
  await apiClient.delete(API.selectiveTouchDeletePage(facilityId, pKey));
}

export async function savePosLayout(
  facilityId: number,
  fKeyPage: number,
  layouts: POSLayout[]
): Promise<void> {
  await apiClient.post(API.selectiveTouchSaveLayout(facilityId, fKeyPage), layouts);
}

export async function reorderPosPages(
  facilityId: number,
  pages: POSPage[]
): Promise<void> {
  await apiClient.post(API.selectiveTouchReorderPages(facilityId), pages);
}

export async function fetchMenuCategoryUsage(
  facilityId: number,
  menuHeaderId: number,
  historyDays = 90
): Promise<MenuCategoryUsage[]> {
  const { data } = await apiClient.get<MenuCategoryUsage[]>(
    API.selectiveTouchMenuCategoryUsage(facilityId),
    { params: { menuHeaderId, historyDays } }
  );
  return data;
}
